import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DespachoInfoCallout,
  DespachoPageShell,
  DespachoSectionCard,
  DespachoSimpleTable,
} from "@/components/despacho/DespachoUi";
import {
  CLIENT_MASTER_BUYER_NOTICE,
  CLIENT_MASTER_SAT_NOTICE,
  clientMasterApi,
  parseClientMasterError,
} from "@/modules/clientMaster/clientMasterApi";
import {
  emptyClientMasterForm,
  formToCreatePayload,
  formToUpdatePayload,
  linkedModulesLabel,
  masterToForm,
} from "@/modules/clientMaster/clientMasterHelpers";
import { useCapabilities } from "@/lib/useCapabilities";
import {
  DESPACHO_READ_ONLY_BADGE,
  canWriteDespacho,
} from "@/modules/despacho/despachoPermissions";

function ClienteMaestroForm({ form, onChange }) {
  const set = (key) => (e) => onChange({ ...form, [key]: e.target.value });
  const setCfg = (key) => (e) =>
    onChange({
      ...form,
      despacho_config: { ...form.despacho_config, [key]: e.target.value },
    });
  const setBool = (key) => (checked) => onChange({ ...form, [key]: checked });
  const setCfgBool = (key) => (checked) =>
    onChange({
      ...form,
      despacho_config: { ...form.despacho_config, [key]: checked },
    });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2 space-y-1.5">
        <Label htmlFor="cm-razon">Razón social *</Label>
        <Input id="cm-razon" value={form.razon_social} onChange={set("razon_social")} maxLength={300} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cm-rfc">RFC (opcional)</Label>
        <Input id="cm-rfc" value={form.rfc} onChange={set("rfc")} className="font-mono text-xs" maxLength={13} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cm-nombre">Nombre comercial</Label>
        <Input id="cm-nombre" value={form.nombre_comercial} onChange={set("nombre_comercial")} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cm-email">Correo principal</Label>
        <Input id="cm-email" type="email" value={form.email_principal} onChange={set("email_principal")} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cm-tel">Teléfono</Label>
        <Input id="cm-tel" value={form.telefono} onChange={set("telefono")} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cm-wa">WhatsApp principal</Label>
        <Input id="cm-wa" value={form.whatsapp_principal} onChange={set("whatsapp_principal")} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cm-contacto">Contacto principal</Label>
        <Input id="cm-contacto" value={form.contacto_principal} onChange={set("contacto_principal")} />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="cm-contable">Contacto contable</Label>
        <Input id="cm-contable" value={form.contacto_contable} onChange={set("contacto_contable")} />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox id="cm-rec" checked={form.acepta_recordatorios} onCheckedChange={setBool("acepta_recordatorios")} />
        <Label htmlFor="cm-rec">Acepta recordatorios</Label>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox id="cm-wa-opt" checked={form.acepta_whatsapp} onCheckedChange={setBool("acepta_whatsapp")} />
        <Label htmlFor="cm-wa-opt">Acepta WhatsApp</Label>
      </div>
      <div className="sm:col-span-2 border-t pt-3 mt-1">
        <p className="text-sm font-medium mb-2">Configuración módulo Despacho</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cm-aux">Auxiliar asignado</Label>
        <Input id="cm-aux" value={form.despacho_config.auxiliar_asignado} onChange={setCfg("auxiliar_asignado")} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cm-honor">Honorario mensual</Label>
        <Input
          id="cm-honor"
          type="number"
          min="0"
          value={form.despacho_config.honorario_mensual}
          onChange={setCfg("honorario_mensual")}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cm-dia">Día de cobro</Label>
        <Input
          id="cm-dia"
          type="number"
          min="1"
          max="31"
          value={form.despacho_config.dia_cobro}
          onChange={setCfg("dia_cobro")}
        />
      </div>
      <div className="flex items-center gap-2 sm:col-span-2">
        <Checkbox
          id="cm-rem"
          checked={form.despacho_config.recordatorios_enabled}
          onCheckedChange={setCfgBool("recordatorios_enabled")}
        />
        <Label htmlFor="cm-rem">Recordatorios despacho habilitados</Label>
      </div>
    </div>
  );
}

export default function ClienteMaestroDespacho() {
  const caps = useCapabilities();
  const canWrite = canWriteDespacho(caps);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyClientMasterForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await clientMasterApi.list({ module_id: "despacho", q: search || undefined });
      setItems(rows);
    } catch (err) {
      const parsed = parseClientMasterError(err);
      toast.error(parsed.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { key: "razon_social", header: "Razón social", render: (r) => r.razon_social || "—" },
      { key: "rfc", header: "RFC", render: (r) => r.rfc || "—" },
      { key: "email", header: "Correo", render: (r) => r.email_principal || "—" },
      { key: "modules", header: "Módulo", render: (r) => linkedModulesLabel(r) },
      { key: "estatus", header: "Estatus", render: (r) => r.estatus || "—" },
      ...(canWrite
        ? [{
            key: "actions",
            header: "Acc.",
            render: (r) => (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditing(r);
                  setForm(masterToForm(r));
                  setDialogOpen(true);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            ),
          }]
        : []),
    ],
    [canWrite],
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyClientMasterForm());
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.razon_social?.trim()) {
      toast.error("La razón social es obligatoria.");
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await clientMasterApi.update(editing.id, formToUpdatePayload(form));
        toast.success("Cliente Maestro actualizado.");
      } else {
        await clientMasterApi.create(formToCreatePayload(form));
        toast.success("Cliente Maestro creado con vínculo Despacho.");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      const parsed = parseClientMasterError(err);
      toast.error(parsed.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DespachoPageShell
      title="Cliente Maestro"
      subtitle="Catálogo unificado de clientes operativos del comprador (fase Despacho)."
      badge={!canWrite ? DESPACHO_READ_ONLY_BADGE : undefined}
    >
      <DespachoInfoCallout>{CLIENT_MASTER_SAT_NOTICE}</DespachoInfoCallout>
      <DespachoInfoCallout className="mt-3">{CLIENT_MASTER_BUYER_NOTICE}</DespachoInfoCallout>

      <DespachoSectionCard
        title="Clientes operativos (Cliente Maestro)"
        description="Alta manual con vínculo al módulo Gestión Despacho. Sin sync Visor/Laboral/Trámites."
        actions={
          canWrite ? (
            <Button type="button" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" />
              Nuevo cliente
            </Button>
          ) : null
        }
      >
        <div className="mb-4 flex gap-2">
          <Input
            placeholder="Buscar RFC o nombre…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
          <Button type="button" variant="outline" onClick={load}>
            Buscar
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando…
          </div>
        ) : (
          <DespachoSimpleTable columns={columns} rows={items} emptyMessage="Aún no hay Cliente Maestro registrado." />
        )}
      </DespachoSectionCard>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Cliente Maestro" : "Nuevo Cliente Maestro"}</DialogTitle>
            <DialogDescription>
              Módulo vinculado: Despacho. No se configura SAT ni descargas automáticas.
            </DialogDescription>
          </DialogHeader>
          <ClienteMaestroForm form={form} onChange={setForm} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving || !canWrite}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DespachoPageShell>
  );
}
